import React, { useMemo } from 'react';
import { FormikProps } from 'formik';

type FormProps = React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>;

interface IProps<TFormValues extends {}> {
    form: FormikProps<TFormValues>;

    disabled?: boolean;
}

export interface IFormContext<TFormValues extends {}> {
    form: FormikProps<TFormValues>;
    disabled: boolean;
}

const FormContext = React.createContext<IFormContext<any> | null>(null);

export const useFormContext = () => {
    const context = React.useContext(FormContext);
    if (!context) throw new Error('No form context. Is there a wrapping provider?');
    return context;
};

function FormContextProvider<TFormValues extends {}>(props: React.PropsWithChildren<IProps<TFormValues> & FormProps>) {
    const { form, children } = props;

    const context = useMemo(() => {
        return {
            form,
            disabled: props.disabled ?? false,
        };
    }, [form, props.disabled]);

    return <FormContext.Provider value={context}>{children}</FormContext.Provider>;
}

export default FormContextProvider;

export const useFieldDisabledState = (props?: { disabled?: boolean | null }) => {
    const formContext = useFormContext();
    return !!(formContext.disabled || !!props?.disabled);
};
